import { z } from "zod";
import { CURRENCY_CODES } from "../money/currency.ts";
import { LOCALE_CODES } from "../i18n/locales.ts";

/**
 * Contracts for `/api/store/**` — the merchant realm.
 *
 * Money is a decimal string of minor units, for the same reason as the admin
 * surface: a JSON number silently rounds past 2^53, and a price is not
 * something to be approximately right about.
 */

const MinorUnits = z.string().regex(/^-?\d+$/, "minor units as a decimal string");
const PositiveMinorUnits = z.string().regex(/^\d+$/, "minor units as a decimal string");

/**
 * How an order reached the restaurant.
 *
 * It lives here rather than beside the order contracts because it is the lower
 * of the two modules: `orders.ts` already imports `DiningStyleSchema` from
 * this file, so declaring it there and reaching back for it closes a cycle
 * that leaves both half-initialised at import time — which surfaces as an
 * unrelated schema failing to construct, not as a message about cycles.
 */
export const ORDER_SOURCES = ["cashier", "staff_app", "qr", "delivery", "back_office"] as const;
export type OrderSource = (typeof ORDER_SOURCES)[number];

/**
 * What an item costs through channels that do not sell at the dining-room
 * price, keyed by the order source. A channel absent from the map sells at
 * `sellPrice`; the map is sparse on purpose, so the ordinary item carries
 * nothing.
 */
const SourcePrices = z.partialRecord(z.enum(ORDER_SOURCES), PositiveMinorUnits);

export const DINING_STYLES = ["dine_in", "take_away", "pre_order", "delivery"] as const;
export const DiningStyleSchema = z.enum(DINING_STYLES);
export type DiningStyle = z.infer<typeof DiningStyleSchema>;

export const SALES_CHANNELS = ["cashier", "qr_ordering", "staff_app", "delivery"] as const;
export const SalesChannelSchema = z.enum(SALES_CHANNELS);

export const PublishStatusSchema = z.enum(["enabled", "disabled"]);
export type PublishStatus = z.infer<typeof PublishStatusSchema>;

/** Per-language overrides for a menu row. Content is translated, not just chrome. */
export const I18nTextSchema = z.partialRecord(
  z.enum(LOCALE_CODES),
  z.object({ name: z.string().optional(), description: z.string().nullable().optional() }),
);

// ── Categories ──────────────────────────────────────────────────────────────

export const CategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  picUrl: z.string().nullable(),
  sort: z.number().int(),
  status: PublishStatusSchema,
  showOnChannels: z.array(SalesChannelSchema),
  minItemNum: z.number().int().nullable(),
  maxItemNum: z.number().int().nullable(),
  itemCount: z.number().int(),
  i18n: I18nTextSchema,
  createdAt: z.string(),
});
export type Category = z.infer<typeof CategorySchema>;

export const CreateCategorySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().nullable().default(null),
  picUrl: z.string().nullable().default(null),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
  showOnChannels: z.array(SalesChannelSchema).default([]),
  minItemNum: z.number().int().nonnegative().nullable().default(null),
  maxItemNum: z.number().int().nonnegative().nullable().default(null),
  i18n: I18nTextSchema.default({}),
});

/** No defaults: `.partial()` keeps them, which turns an omitted key into a reset. */
export const UpdateCategorySchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().nullable().optional(),
  picUrl: z.string().nullable().optional(),
  sort: z.number().int().optional(),
  status: PublishStatusSchema.optional(),
  showOnChannels: z.array(SalesChannelSchema).optional(),
  minItemNum: z.number().int().nonnegative().nullable().optional(),
  maxItemNum: z.number().int().nonnegative().nullable().optional(),
  i18n: I18nTextSchema.optional(),
});

// ── Items ───────────────────────────────────────────────────────────────────

export const ITEM_TYPES = ["normal", "meal_set", "temporary", "pre_order"] as const;
export const PRICING_METHODS = ["unit", "weight"] as const;

/**
 * Where an item gets made, and which of that station's printers it goes to.
 *
 * One field rather than a `stationIds` array beside a printer map, because two
 * fields can disagree — a map naming a station the array does not — and that
 * state should be unrepresentable rather than validated.
 *
 * **Read and written asymmetrically, on purpose.** On the way out `printerIds`
 * is *resolved*: an item that named no printer comes back with every printer
 * standing at that station, so a client never has to know that empty storage
 * means "all". On the way in, an array equal to the station's full set is
 * stored as no rows, and an empty array stores no rows too — "route here and
 * print nowhere" is not expressible, and the way to say it is a station that
 * has no printers.
 */
export const ItemStationSchema = z.object({
  stationId: z.uuid(),
  printerIds: z.array(z.uuid()),
});
export type ItemStation = z.infer<typeof ItemStationSchema>;

export const ItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  number: z.string().nullable(),
  description: z.string().nullable(),
  note: z.string().nullable(),

  sellPrice: MinorUnits,
  secondCurrencySellPrice: MinorUnits.nullable(),
  vipPrice: MinorUnits.nullable(),
  withVipPrice: z.boolean(),
  discountPrice: MinorUnits.nullable(),
  discountRateBp: z.number().int().nullable(),
  isPricedAtTill: z.boolean(),
  pricingMethod: z.enum(PRICING_METHODS),
  packingFee: MinorUnits,
  showPackingFee: z.boolean(),

  picUrl: z.string().nullable(),
  labels: z.array(z.string()),
  status: PublishStatusSchema,
  soldOut: z.boolean(),
  autoSoldOut: z.boolean(),
  diningStyles: z.array(DiningStyleSchema),
  showOnChannels: z.array(SalesChannelSchema),
  /**
   * Which restricted tenders may pay for this item.
   *
   * Free strings the merchant chooses, matched against a payment method's
   * `restrictedToClass`. Not an enum, because what qualifies for a
   * food-assistance programme is a market's law rather than ours.
   */
  eligibilityClasses: z.array(z.string()),
  type: z.enum(ITEM_TYPES),

  serviceChargeWhitelisting: z.boolean(),
  serviceChargePercentageBp: z.number().int().nullable(),
  vatWhitelisting: z.boolean(),
  discountItemWhitelisting: z.boolean(),
  commissionPercentageBp: z.number().int().nullable(),

  stations: z.array(ItemStationSchema),
  madeToOrder: z.boolean(),
  printLabel: z.boolean(),
  restrictNum: z.number().int(),
  prepaymentPercentageBp: z.number().int(),
  salesVolume: z.number().int(),
  sort: z.number().int(),

  categoryIds: z.array(z.uuid()),
  sourcePrices: SourcePrices,
  specificationGroupIds: z.array(z.uuid()),
  toppingTemplateIds: z.array(z.uuid()),
  tagTemplateIds: z.array(z.uuid()),
  i18n: I18nTextSchema,
  createdAt: z.string(),
});
export type Item = z.infer<typeof ItemSchema>;

export const CreateItemSchema = z.object({
  name: z.string().min(1).max(200),
  number: z.string().max(60).nullable().default(null),
  description: z.string().nullable().default(null),
  note: z.string().nullable().default(null),

  sellPrice: PositiveMinorUnits.default("0"),
  secondCurrencySellPrice: PositiveMinorUnits.nullable().default(null),
  vipPrice: PositiveMinorUnits.nullable().default(null),
  withVipPrice: z.boolean().default(false),
  discountPrice: PositiveMinorUnits.nullable().default(null),
  discountRateBp: z.number().int().min(0).max(10_000).nullable().default(null),
  isPricedAtTill: z.boolean().default(false),
  pricingMethod: z.enum(PRICING_METHODS).default("unit"),
  packingFee: PositiveMinorUnits.default("0"),
  showPackingFee: z.boolean().default(false),

  picUrl: z.string().nullable().default(null),
  labels: z.array(z.string()).default([]),
  status: PublishStatusSchema.default("enabled"),
  soldOut: z.boolean().default(false),
  autoSoldOut: z.boolean().default(false),
  diningStyles: z.array(DiningStyleSchema).default([]),
  showOnChannels: z.array(SalesChannelSchema).default([]),
  eligibilityClasses: z.array(z.string().min(1).max(64)).default([]),
  type: z.enum(ITEM_TYPES).default("normal"),

  serviceChargeWhitelisting: z.boolean().default(false),
  serviceChargePercentageBp: z.number().int().min(0).max(10_000).nullable().default(null),
  vatWhitelisting: z.boolean().default(false),
  discountItemWhitelisting: z.boolean().default(false),
  commissionPercentageBp: z.number().int().min(0).max(10_000).nullable().default(null),

  stations: z.array(ItemStationSchema).default([]),
  madeToOrder: z.boolean().default(false),
  printLabel: z.boolean().default(false),
  restrictNum: z.number().int().min(0).default(0),
  prepaymentPercentageBp: z.number().int().min(0).max(10_000).default(0),
  sort: z.number().int().default(0),

  categoryIds: z.array(z.uuid()).default([]),
  sourcePrices: SourcePrices.default({}),
  specificationGroupIds: z.array(z.uuid()).default([]),
  toppingTemplateIds: z.array(z.uuid()).default([]),
  tagTemplateIds: z.array(z.uuid()).default([]),
  i18n: I18nTextSchema.default({}),
});
export type CreateItem = z.infer<typeof CreateItemSchema>;

export const UpdateItemSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  number: z.string().max(60).nullable().optional(),
  description: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  sellPrice: PositiveMinorUnits.optional(),
  secondCurrencySellPrice: PositiveMinorUnits.nullable().optional(),
  vipPrice: PositiveMinorUnits.nullable().optional(),
  withVipPrice: z.boolean().optional(),
  discountPrice: PositiveMinorUnits.nullable().optional(),
  discountRateBp: z.number().int().min(0).max(10_000).nullable().optional(),
  isPricedAtTill: z.boolean().optional(),
  pricingMethod: z.enum(PRICING_METHODS).optional(),
  packingFee: PositiveMinorUnits.optional(),
  showPackingFee: z.boolean().optional(),
  picUrl: z.string().nullable().optional(),
  labels: z.array(z.string()).optional(),
  status: PublishStatusSchema.optional(),
  soldOut: z.boolean().optional(),
  autoSoldOut: z.boolean().optional(),
  diningStyles: z.array(DiningStyleSchema).optional(),
  showOnChannels: z.array(SalesChannelSchema).optional(),
  eligibilityClasses: z.array(z.string().min(1).max(64)).optional(),
  type: z.enum(ITEM_TYPES).optional(),
  serviceChargeWhitelisting: z.boolean().optional(),
  serviceChargePercentageBp: z.number().int().min(0).max(10_000).nullable().optional(),
  vatWhitelisting: z.boolean().optional(),
  discountItemWhitelisting: z.boolean().optional(),
  commissionPercentageBp: z.number().int().min(0).max(10_000).nullable().optional(),
  stations: z.array(ItemStationSchema).optional(),
  madeToOrder: z.boolean().optional(),
  printLabel: z.boolean().optional(),
  restrictNum: z.number().int().min(0).optional(),
  prepaymentPercentageBp: z.number().int().min(0).max(10_000).optional(),
  sort: z.number().int().optional(),
  categoryIds: z.array(z.uuid()).optional(),
  sourcePrices: SourcePrices.optional(),
  specificationGroupIds: z.array(z.uuid()).optional(),
  toppingTemplateIds: z.array(z.uuid()).optional(),
  tagTemplateIds: z.array(z.uuid()).optional(),
  i18n: I18nTextSchema.optional(),
});

/** Bulk status, price and category changes from the item list. */
export const BatchItemSchema = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  status: PublishStatusSchema.optional(),
  soldOut: z.boolean().optional(),
  addCategoryIds: z.array(z.uuid()).optional(),
  removeCategoryIds: z.array(z.uuid()).optional(),
});

/** The cheap staleness check the till polls before downloading a menu. */
export const MenuVersionSchema = z.object({ version: z.string() });

// ── Modifiers ───────────────────────────────────────────────────────────────

export const MODIFIER_SELECTIONS = ["single", "multiple"] as const;

/**
 * Every modifier a customer reads carries the same `i18n` map an item does,
 * on the group and on each option: a Khmer dish name over English size buttons
 * is half a menu, and the order line snapshots the option's *name*, so English
 * there is frozen onto the ticket and the receipt.
 *
 * Only `name` is meaningful — none of these tables has a description column —
 * but the shape is the shared one rather than a second, nearly identical
 * schema for the editor to special-case.
 *
 * The translations and each child's `status` ride in the save body because
 * there is nothing to address them by afterwards: the handler hard-deletes and
 * re-inserts the options on every save, so a child's id does not survive the
 * request that wrote it. A body that leaves `status` out is a body that asks
 * for the column's `enabled` default, which is why the save carries it: a
 * hidden option would otherwise come back on the next save of its parent.
 */

/**
 * How many live menu items link this template.
 *
 * Derived, so it is on the three read schemas and on none of the `Save*` ones:
 * a template is linked from the item, on `/menu/items`, and never from here.
 * It answers the question the catalogue could not — a template nothing links
 * is unreachable from the till, and the screen used to give no sign of it.
 */
const ModifierItemCount = z.number().int();

export const SpecificationGroupSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  selection: z.enum(MODIFIER_SELECTIONS),
  isRequired: z.boolean(),
  sort: z.number().int(),
  status: PublishStatusSchema,
  itemCount: ModifierItemCount,
  i18n: I18nTextSchema,
  options: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      priceDelta: MinorUnits,
      isDefault: z.boolean(),
      sort: z.number().int(),
      status: PublishStatusSchema,
      i18n: I18nTextSchema,
    }),
  ),
});

export const SaveSpecificationGroupSchema = z.object({
  name: z.string().min(1).max(120),
  selection: z.enum(MODIFIER_SELECTIONS).default("single"),
  isRequired: z.boolean().default(true),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
  i18n: I18nTextSchema.default({}),
  options: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        priceDelta: MinorUnits.default("0"),
        isDefault: z.boolean().default(false),
        sort: z.number().int().default(0),
        status: PublishStatusSchema.default("enabled"),
        i18n: I18nTextSchema.default({}),
      }),
    )
    .min(1),
});

export const ToppingTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  selection: z.enum(MODIFIER_SELECTIONS),
  isStoreDefault: z.boolean(),
  sort: z.number().int(),
  status: PublishStatusSchema,
  itemCount: ModifierItemCount,
  i18n: I18nTextSchema,
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      price: MinorUnits,
      defaultSelect: z.boolean(),
      sort: z.number().int(),
      status: PublishStatusSchema,
      i18n: I18nTextSchema,
    }),
  ),
});

export const SaveToppingTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  selection: z.enum(MODIFIER_SELECTIONS).default("multiple"),
  isStoreDefault: z.boolean().default(false),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
  i18n: I18nTextSchema.default({}),
  items: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        price: PositiveMinorUnits.default("0"),
        defaultSelect: z.boolean().default(false),
        sort: z.number().int().default(0),
        status: PublishStatusSchema.default("enabled"),
        i18n: I18nTextSchema.default({}),
      }),
    )
    .min(1),
});

export const TagTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  selection: z.enum(MODIFIER_SELECTIONS),
  sort: z.number().int(),
  status: PublishStatusSchema,
  itemCount: ModifierItemCount,
  i18n: I18nTextSchema,
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      price: MinorUnits,
      defaultSelect: z.boolean(),
      sort: z.number().int(),
      status: PublishStatusSchema,
      i18n: I18nTextSchema,
    }),
  ),
});

export const SaveTagTemplateSchema = z.object({
  name: z.string().min(1).max(120),
  selection: z.enum(MODIFIER_SELECTIONS).default("multiple"),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
  i18n: I18nTextSchema.default({}),
  items: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        // Priced tags are legal: "extra spicy, +$0.50" is a real menu line.
        price: PositiveMinorUnits.default("0"),
        defaultSelect: z.boolean().default(false),
        sort: z.number().int().default(0),
        status: PublishStatusSchema.default("enabled"),
        i18n: I18nTextSchema.default({}),
      }),
    )
    .min(1),
});

/**
 * One menu item that links a modifier template — what `itemCount` counts, named.
 *
 * The price is here because the question behind the list is usually whether a
 * template is worth keeping, and a dish's price is what makes that decision.
 * There is no currency field: the whole menu surface is priced in the store's
 * own, which the screen already holds, and a per-row copy would cost the
 * handler a `core_stores` read to say the same thing on every line.
 */
export const ModifierConsumerSchema = z.object({
  itemId: z.uuid(),
  itemName: z.string(),
  price: MinorUnits,
  /** A hidden item still links the template, and the list says so. */
  status: PublishStatusSchema,
});

export const QuickNoteSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sort: z.number().int(),
  status: PublishStatusSchema,
});

export const SaveQuickNoteSchema = z.object({
  name: z.string().min(1).max(120),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
});

// ── Tables ──────────────────────────────────────────────────────────────────

export const TableAreaSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isDefault: z.boolean(),
  sort: z.number().int(),
  tableCount: z.number().int(),
});

export const SaveTableAreaSchema = z.object({
  name: z.string().min(1).max(120),
  isDefault: z.boolean().default(false),
  sort: z.number().int().default(0),
});

export const TABLE_STATUSES = ["free", "seated", "ordered", "bill_requested"] as const;
export type TableStatus = (typeof TABLE_STATUSES)[number];

export const DiningTableSchema = z.object({
  id: z.uuid(),
  areaId: z.uuid(),
  areaName: z.string(),
  name: z.string(),
  capacity: z.number().int(),
  qrToken: z.string(),
  /**
   * Derived from the table's open bill, never stored: no bill is `free`, a
   * bill with no live line is `seated`, one with a live line is `ordered`.
   * A stored flag would need every close, merge, split and reversal to
   * remember to clear it, and the one that forgot would strand a table.
   */
  status: z.enum(TABLE_STATUSES),
  /**
   * The bill occupying the table, so tapping an occupied one opens it rather
   * than starting a second. The id alone: a table row is readable with
   * `tables:read`, and what is *on* the bill needs `orders:read`.
   */
  openOrderId: z.uuid().nullable(),
  sort: z.number().int(),
});

export const SaveDiningTableSchema = z.object({
  areaId: z.uuid(),
  name: z.string().min(1).max(60),
  capacity: z.number().int().min(1).max(100).default(2),
  sort: z.number().int().default(0),
});

export const UpdateDiningTableSchema = z.object({
  areaId: z.uuid().optional(),
  name: z.string().min(1).max(60).optional(),
  capacity: z.number().int().min(1).max(100).optional(),
  sort: z.number().int().optional(),
});

export const TempCodeConfigSchema = z.object({
  isEnabled: z.boolean(),
  prefix: z.string(),
  rangeStart: z.number().int(),
  rangeEnd: z.number().int(),
});

export const SaveTempCodeConfigSchema = z
  .object({
    isEnabled: z.boolean(),
    prefix: z.string().min(1).max(4),
    rangeStart: z.number().int().min(1),
    rangeEnd: z.number().int().min(1),
  })
  .refine((v) => v.rangeEnd >= v.rangeStart, {
    message: "The range must end at or after it starts",
    path: ["rangeEnd"],
  });

// ── Store configuration ─────────────────────────────────────────────────────

/**
 * Which of the two dates a store's statistics read.
 *
 * A separate decision from the cutoff below, not a rewording of it: the cutoff
 * decides which trading day a bill is *stamped* with, this decides which of the
 * two stamped dates a report *reads*. A store can keep a 3am cutoff on its
 * orders and still close its books on calendar months.
 */
export const STATISTICS_BASES = ["business_day", "calendar_day"] as const;
export const StatisticsBasisSchema = z.enum(STATISTICS_BASES);
export type StatisticsBasis = z.infer<typeof StatisticsBasisSchema>;

export const StoreProfileSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  timezone: z.string(),
  businessDayCutoff: z.string(),
  statisticsBasis: StatisticsBasisSchema,
  languages: z.array(z.enum(LOCALE_CODES)),
  primaryCurrency: z.enum(CURRENCY_CODES),
  secondCurrency: z.enum(CURRENCY_CODES).nullable(),
  exchangeRate: z.string(),
  cashRoundingIncrement: z.string(),
  isMain: z.boolean(),
  marketProfileCode: z.string(),
});

export type StoreProfile = z.infer<typeof StoreProfileSchema>;

export const UpdateStoreProfileSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  address: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  timezone: z.string().optional(),
  /** When a restaurant's day rolls over. Not midnight. */
  businessDayCutoff: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/)
    .optional(),
  /** Which of the two dates every report reads. Not the cutoff above. */
  statisticsBasis: StatisticsBasisSchema.optional(),
  languages: z.array(z.enum(LOCALE_CODES)).min(1).optional(),
});

export const UpdateCurrencySchema = z.object({
  secondCurrency: z.enum(CURRENCY_CODES).nullable().optional(),
  /** Scaled by EXCHANGE_RATE_SCALE. */
  exchangeRate: PositiveMinorUnits.optional(),
  cashRoundingIncrement: PositiveMinorUnits.optional(),
});

export const CurrencyRateLogSchema = z.object({
  id: z.uuid(),
  fromCurrency: z.string(),
  toCurrency: z.string(),
  rate: z.string(),
  effectiveFrom: z.string(),
  changedByName: z.string().nullable(),
});

// ── Tax ─────────────────────────────────────────────────────────────────────

export const TaxRateSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  rateBp: z.number().int(),
  isInclusive: z.boolean(),
  sequence: z.number().int(),
  isCompound: z.boolean(),
  appliesToServiceCharge: z.boolean(),
  appliesToTableFee: z.boolean(),
  categoryScope: z.array(z.uuid()),
  diningStyleScope: z.array(DiningStyleSchema),
  jurisdiction: z.string().nullable(),
  /**
   * Rules are versioned by date rather than flagged active: a settled order
   * resolves against its own business date, so retiring a rule is setting
   * `effectiveTo` and never mutating what a past bill was taxed at.
   */
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
});

export const SaveTaxRateSchema = z
  .object({
    code: z.string().min(1).max(40),
    name: z.string().min(1).max(120),
    /** Basis points. A rate is never a float and never rounded. */
    rateBp: z.number().int().min(0).max(100_000),
    isInclusive: z.boolean(),
    sequence: z.number().int().min(1).default(1),
    isCompound: z.boolean().default(false),
    appliesToServiceCharge: z.boolean().default(true),
    appliesToTableFee: z.boolean().default(true),
    categoryScope: z.array(z.uuid()).default([]),
    diningStyleScope: z.array(DiningStyleSchema).default([]),
    jurisdiction: z.string().nullable().default(null),
    /** Defaults to now, so the simple case does not have to think about dates. */
    effectiveFrom: z.iso.datetime().optional(),
    effectiveTo: z.iso.datetime().nullable().default(null),
  })
  /**
   * The pricing engine refuses this pair rather than guessing at the
   * arithmetic. It is refused here too so the merchant learns when they save
   * it, rather than at the next settlement as a 500 on somebody's bill.
   */
  .refine((v) => !(v.isInclusive && v.isCompound), {
    message: "An inclusive rate is already inside the price, so it cannot also compound",
    path: ["isCompound"],
  });

export const ROUNDING_MODE_VALUES = [
  "half_up",
  "half_even",
  "half_down",
  "up",
  "down",
  "ceiling",
  "floor",
] as const;

/**
 * Whether tax is *shown* is a third axis, and it is deliberately absent here:
 * `spec/raw/README.md` correction 3 puts display on the receipt template,
 * because it is a print decision that must never reach the arithmetic.
 */
export const StoreTaxSettingsSchema = z.object({
  chargeTax: z.boolean(),
  /**
   * Whether a whole-bill discount is applied before or after tax. Only the
   * before-tax branch is implemented; the engine refuses the other rather than
   * guessing at money.
   */
  includeTaxInWholeDiscount: z.boolean(),
  /** What this tax is called here: VAT, Sales Tax, GST. Never hard-coded. */
  taxLabel: z.string(),
  serviceChargeCalculation: z.enum(["on_subtotal", "per_unit"]),
  serviceChargeRounding: z.enum(ROUNDING_MODE_VALUES),
  serviceChargeDiningStyleScope: z.array(DiningStyleSchema),
  taxRounding: z.enum(ROUNDING_MODE_VALUES),
  lineRounding: z.enum(ROUNDING_MODE_VALUES),
});

export const UpdateStoreTaxSettingsSchema = z.object({
  chargeTax: z.boolean().optional(),
  includeTaxInWholeDiscount: z.boolean().optional(),
  taxLabel: z.string().min(1).max(40).optional(),
  serviceChargeCalculation: z.enum(["on_subtotal", "per_unit"]).optional(),
  serviceChargeRounding: z.enum(ROUNDING_MODE_VALUES).optional(),
  serviceChargeDiningStyleScope: z.array(DiningStyleSchema).optional(),
  taxRounding: z.enum(ROUNDING_MODE_VALUES).optional(),
  lineRounding: z.enum(ROUNDING_MODE_VALUES).optional(),
});

export const ServiceChargeRateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  rateBp: z.number().int(),
  isDefault: z.boolean(),
  diningStyleScope: z.array(DiningStyleSchema),
  sort: z.number().int(),
  status: PublishStatusSchema,
});

export const SaveServiceChargeRateSchema = z.object({
  name: z.string().min(1).max(120),
  rateBp: z.number().int().min(0).max(10_000),
  isDefault: z.boolean().default(false),
  diningStyleScope: z.array(DiningStyleSchema).default([]),
  sort: z.number().int().default(0),
  status: PublishStatusSchema.default("enabled"),
});

export const TableFeeSettingsSchema = z.object({
  isEnabled: z.boolean(),
  basis: z.enum(["per_head", "per_table"]),
  amount: MinorUnits,
  diningStyleScope: z.array(DiningStyleSchema),
});

export const SaveTableFeeSettingsSchema = z.object({
  isEnabled: z.boolean(),
  basis: z.enum(["per_head", "per_table"]),
  amount: PositiveMinorUnits,
  diningStyleScope: z.array(DiningStyleSchema).default([]),
});

/**
 * Tipping (`STORE_SETTINGS_TIPPING`).
 *
 * Deliberately its own shape rather than a variant of the service charge: 29
 * CFR 531.55 makes them different instruments, and a merchant may run both.
 */
export const TipSettingsSchema = z.object({
  isEnabled: z.boolean(),
  /** Basis points, offered in this order. Empty offers the keypad alone. */
  suggestedRatesBp: z.array(z.number().int().min(0).max(10_000)),
  /** Whether a suggested percentage is taken of the subtotal or of the total. */
  suggestOnPreTaxSubtotal: z.boolean(),
  diningStyleScope: z.array(DiningStyleSchema),
});

export const SaveTipSettingsSchema = z.object({
  isEnabled: z.boolean(),
  // Four buttons is the most a settle screen can offer without the keypad
  // becoming the harder path, which defeats the point of suggesting.
  suggestedRatesBp: z.array(z.number().int().min(0).max(10_000)).max(4).default([]),
  suggestOnPreTaxSubtotal: z.boolean().default(false),
  diningStyleScope: z.array(DiningStyleSchema).default([]),
});

export const DiscountExclusionsSchema = z.object({ itemIds: z.array(z.uuid()) });

export const PaymentMethodSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  isSelected: z.boolean(),
  isOffline: z.boolean(),
  providerCode: z.string().nullable(),
  /**
   * The class of goods this method may pay for, or null for anything.
   *
   * A restricted tender — an instrument that is money only for a particular
   * kind of thing, which is what food-assistance cards are. The class is a
   * merchant's own string matched against an item's `eligibilityClasses`,
   * never a programme name: those differ per market and putting one here
   * would be a migration per market.
   */
  restrictedToClass: z.string().nullable(),
  /**
   * Whether the product ships this tender, as opposed to the merchant creating
   * it. A built-in carries behaviour on its `code` — the drawer, the tab
   * ledger, the shift's cash reconciliation — so it may be renamed and turned
   * off but never deleted.
   */
  isBuiltIn: z.boolean(),
  sort: z.number().int(),
});

export const SavePaymentMethodsSchema = z.object({
  methods: z
    .array(
      z.object({
        id: z.uuid(),
        /**
         * The merchant's word for this tender, on the till and on every report
         * that groups takings. Editable on a built-in too: "Cash" is English,
         * and the drawer opens on the code rather than on what it is called.
         */
        name: z.string().min(1).max(64),
        isSelected: z.boolean(),
        /**
         * Stated, never defaulted. This body names the rows it edits, so an
         * omitted field means "leave it" to whoever wrote the client and
         * "reset it" to the handler — which is how toggling a payment method
         * silently cleared the class a restricted tender is gated on, and with
         * it the eligibility check. Required is the only spelling where those
         * two readings cannot diverge.
         */
        providerCode: z.string().nullable(),
        restrictedToClass: z.string().min(1).max(64).nullable(),
        sort: z.number().int(),
      }),
    )
    .min(1),
});

/**
 * A tender the merchant adds themselves.
 *
 * Only a name: the code is minted by the server, because a code decides
 * behaviour and a merchant naming one `CASH` would open a drawer for a bank
 * transfer. Everything else — whether the till offers it, its order, a provider,
 * a restricted class — is set afterwards through the bulk save, which already
 * gates the fields that need a feature.
 */
export const CreatePaymentMethodSchema = z.object({
  name: z.string().min(1).max(64),
});

export type TableArea = z.infer<typeof TableAreaSchema>;

export type DiningTable = z.infer<typeof DiningTableSchema>;

export type SpecificationGroup = z.infer<typeof SpecificationGroupSchema>;

export type ToppingTemplate = z.infer<typeof ToppingTemplateSchema>;

export type TagTemplate = z.infer<typeof TagTemplateSchema>;

export type ModifierConsumer = z.infer<typeof ModifierConsumerSchema>;

export type QuickNote = z.infer<typeof QuickNoteSchema>;

export type TaxRate = z.infer<typeof TaxRateSchema>;

export type StoreTaxSettings = z.infer<typeof StoreTaxSettingsSchema>;

export type ServiceChargeRate = z.infer<typeof ServiceChargeRateSchema>;

export type TableFeeSettings = z.infer<typeof TableFeeSettingsSchema>;
export type TipSettings = z.infer<typeof TipSettingsSchema>;

export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export type TempCodeConfig = z.infer<typeof TempCodeConfigSchema>;

export type CurrencyRateLogEntry = z.infer<typeof CurrencyRateLogSchema>;

/**
 * Invoice numbering and the tax identity printed on a receipt
 * (`12-chain.md` § 2). The source promises the serial "will print in a
 * continuous sequence", and validates it as 001–999 against an exchange rate
 * of 4000–4200 — both Cambodia-shaped, so the range comes from the market
 * profile and `limits` carries it to the form rather than a constant in the UI.
 */
export const INVOICE_DATE_TYPES = ["none", "year", "date"] as const;

export const InvoiceSettingsSchema = z.object({
  isEnabled: z.boolean(),
  prefix: z.string(),
  dateType: z.enum(INVOICE_DATE_TYPES),
  /** Serialised as a string: it is a bigint. */
  nextNumber: z.string(),
  /** What the next invoice will read, assembled server-side so both agree. */
  preview: z.string(),
  taxRegistrationName: z.string().nullable(),
  taxRegistrationNumber: z.string().nullable(),
  taxAddress: z.string().nullable(),
  /** The tax authority's number for the machine, where a market registers one. */
  fiscalDeviceNumber: z.string().nullable(),
  limits: z.object({
    serialMin: z.number().int().nullable(),
    serialMax: z.number().int().nullable(),
  }),
});

export type InvoiceSettings = z.infer<typeof InvoiceSettingsSchema>;

export const UpdateInvoiceSettingsSchema = z.object({
  isEnabled: z.boolean().optional(),
  prefix: z.string().max(12).optional(),
  dateType: z.enum(INVOICE_DATE_TYPES).optional(),
  nextNumber: z.string().regex(/^\d+$/, "digits only").optional(),
  taxRegistrationName: z.string().max(160).nullable().optional(),
  taxRegistrationNumber: z.string().max(60).nullable().optional(),
  taxAddress: z.string().max(240).nullable().optional(),
  fiscalDeviceNumber: z.string().max(60).nullable().optional(),
});

// ── Bearer gift cards ───────────────────────────────────────────────────────

/**
 * A gift card belongs to **whoever is holding it**, which is what makes it a
 * present and what separates it from a member's stored value. Stored value is
 * found by phone number and guarded by a PIN; here the code is the credential.
 */
export const GiftCardSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  initialAmount: MinorUnits,
  balance: MinorUnits,
  currency: z.string(),
  status: z.enum(["active", "empty", "void"]),
  expiresAt: z.string().nullable(),
  issuedStoreId: z.uuid().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type GiftCard = z.infer<typeof GiftCardSchema>;

export const IssueGiftCardSchema = z.object({
  amount: PositiveMinorUnits,
  /** Null never expires, which is the law in several US states. */
  expiresAt: z.string().nullable().default(null),
  note: z.string().max(240).nullable().default(null),
});

export const TopUpGiftCardSchema = z.object({
  amount: PositiveMinorUnits,
  note: z.string().max(240).nullable().default(null),
});

/** Reported lost or stolen. Refuses redemption and says so. */
export const VoidGiftCardSchema = z.object({ reason: z.string().min(1).max(240) });

export const GiftCardMovementSchema = z.object({
  id: z.uuid(),
  amount: MinorUnits,
  balanceAfter: MinorUnits,
  storeId: z.uuid().nullable(),
  orderId: z.uuid().nullable(),
  actorName: z.string().nullable(),
  reason: z.string().nullable(),
  occurredAt: z.string(),
});
